import { Component } from '@theme/component';

/**
 * A small utility for setting up an animated disclosure panel using inert and CSS display grid for height auto animation.
 *
 * This component works with any element that follows the details/summary pattern for revealing content.
 * Forms and other interactive elements don't need to live inside a details element, but can still use
 * the expanded pattern for accessibility and animation.
 *
 * The component relies on CSS display grid and inert attributes to create smooth height animations
 * when expanding/collapsing content areas.
 *
 * @class DisclosureCustom
 * @typedef {object} Refs
 * @property {HTMLElement} disclosureTrigger – The disclosure trigger element.
 * @property {HTMLElement} disclosureContent – The disclosure content element.
 *
 * @extends Component<Refs>
 */
class DisclosureCustom extends Component {
  requiredRefs = ['disclosureTrigger', 'disclosureContent'];

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #fallbackTimer;

  connectedCallback() {
    super.connectedCallback();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    clearTimeout(this.#fallbackTimer);
  }

  /**
   * Toggles the disclosure panel open/closed state.
   * Updates aria-expanded, aria-label attributes on the trigger and inert state on content.
   * Works with CSS grid animations to provide smooth height transitions.
   */
  toggleDisclosure = () => {
    const { disclosureTrigger: trigger, disclosureContent: content } = this.refs;
    const expanded = trigger.matches('[aria-expanded="true"]');
    trigger.setAttribute('aria-expanded', String(!expanded));
    trigger.setAttribute(
      'aria-label',
      `${expanded ? trigger.dataset.disclosureOpen : trigger.dataset.disclosureClose}`
    );
    content.inert = expanded;

    // FIX (Micky - iPhone 14 disclosure bug): on at least one real device (an
    // iPhone 14 on a current iOS/Safari version) this panel's CSS
    // grid-template-rows/opacity transition has failed to resolve to its open
    // state even though `inert` toggles correctly here - the panel stays
    // visually collapsed. We could not reproduce this ourselves (our test
    // tooling is Chrome-only), so rather than guess at the exact WebKit
    // rendering mechanism involved, we verify the outcome shortly after the
    // transition should have finished and self-heal if it didn't take. This
    // is a no-op wherever the CSS already works correctly - it only forces
    // the open state when we can detect it genuinely failed to apply, and it
    // never touches aria-expanded/inert, so accessibility behavior is
    // unaffected either way.
    clearTimeout(this.#fallbackTimer);
    if (expanded) {
      // Closing: clear any inline override left behind by the fallback below.
      content.style.removeProperty('grid-template-rows');
      content.style.removeProperty('opacity');
    } else {
      const durationMs = (parseFloat(getComputedStyle(content).transitionDuration) || 0) * 1000;
      this.#fallbackTimer = setTimeout(() => {
        if (!content.hasAttribute('inert') && content.getBoundingClientRect().height < 1 && content.scrollHeight > 0) {
          content.style.setProperty('grid-template-rows', '1fr');
          content.style.setProperty('opacity', '1');
        }
      }, durationMs + 50);
    }
  };
}

if (!customElements.get('disclosure-custom')) {
  customElements.define('disclosure-custom', DisclosureCustom);
}
